/**
 * Date helpers shared by the event form and the search-result importers.
 */

/** ISO date (or Date) -> "YYYY-MM-DD" for a date input. '' when unparseable. */
export const formatDateForInput = (dateString) => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    return date.toISOString().split('T')[0]
  } catch (e) {
    return ''
  }
}

/**
 * Is this event spread over more than one calendar day?
 *
 * `spanMultipleDays` is a form-only checkbox — it exists in the client's event
 * state and on no server model. Importers used to carry it across with
 * `source.spanMultipleDays || false`, which reads a field the source never has,
 * so pulling in a three-day festival always produced an unchecked box and an
 * event the rest of the form treated as single-day.
 *
 * Deriving it from the dates is the only thing that can be correct, because the
 * dates are the actual source of truth.
 */
export const spansMultipleDays = (startDate, endDate) => {
  const start = formatDateForInput(startDate)
  const end = formatDateForInput(endDate)

  if (!start || !end) return false
  return start !== end
}

// A festival longer than this is almost certainly bad data — a swapped or
// mistyped year would otherwise generate thousands of day rows.
const MAX_EVENT_DAYS = 30

/**
 * Expands a date range into one entry per day, in the `additionalDates` shape
 * the event form uses.
 *
 * Imported multi-day events used to arrive with a start and end date but no
 * days, so the per-day UI (which artist plays when) had nothing to attach to
 * and the user had to type each day by hand.
 */
export const buildEventDays = (startDate, endDate) => {
  const start = formatDateForInput(startDate)
  const end = formatDateForInput(endDate) || start

  if (!start) return []

  const days = []
  const cursor = new Date(`${start}T12:00:00Z`)
  const last = new Date(`${end}T12:00:00Z`)

  if (isNaN(cursor.getTime()) || isNaN(last.getTime()) || last < cursor) return []

  while (cursor <= last && days.length < MAX_EVENT_DAYS) {
    days.push({
      date: cursor.toISOString().split('T')[0],
      description: `Day ${days.length + 1}`
    })
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return days
}
